import "./style.scss";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Collapse, FormControlLabel, FormLabel, Radio, RadioGroup } from "@mui/material";
import Plus from "assets/img/svg/plus";
import type { TAdvancePayment } from "components/AdvanceManagement/NewAdvanceModal";
import CurrencyIcon from "components/shared/CurrencyIcon";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import DateFormComponent from "components/shared/FormComponent/DateFormComponent";
import NumberFormComponent from "components/shared/FormComponent/NumberFormComponent";
import RadioFormComponent from "components/shared/FormComponent/RadioFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import InfoTooltip from "components/shared/InfoTooltip";
import NewGroupModal from "components/shared/NewGroupModal";
import PeriodName from "components/shared/PeriodName";
import TermCustomFields from "../TermCustomFields";
import ScrollShadowWrapper from "components/shared/ScrollShadowWrapper";
import TextButton from "components/shared/TextButton";
import TitleSeparation from "components/shared/TitleSeparation";
import type { IBeneficiar } from "containers/Beneficiars/types";
import { useCompanySettings } from "hooks/useCompanySettings";
import type { CSSProperties, Dispatch, SetStateAction } from "react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IAdvanceSlice } from "store/advancePaymentSlice";
import type { TermGroup } from "store/beneficiaryStore";
import type { TPeriod } from "store/periodStore";

import variables from "../../../assets/styles/_variables.module.scss";
import type { ITermFormData } from "..";
import {
    clearTitlePublishedBeforePeriodFields,
    getTermFormMinAdvancePaymentDate,
    shouldClearTitlePublishedBeforePeriodFields,
} from "../utils";
import InlineAdvanceForm from "./AdvancePaymentSection/InlineAdvanceForm";
import OpeningBalanceField from "./AdvancePaymentSection/OpeningBalanceField";
import "./AdvancePaymentSection/style.scss";

interface ISettingsStep {
    /**
     * Data and functions from the useForm hook needed in this component.
     */
    useFormData: ITermFormData;
    formData: {
        /**
         * Value from the Advance input > used to display warning.
         */
        chosenAdvance: string | number;
        /**
         * Advance group chosen
         */
        chosenAdvanceGroup: TermGroup | null | undefined;
        /**
         * Beneficiary chosen within form.
         */
        chosenBeneficiary: IBeneficiar | null;
        /**
         * Value from the initial advance input
         */
        chosenInitialAdvance: number;
        chosenCumulativeRights: string | number;
    };
    /**
     * Index of the current step.
     */
    indexStep: number;
    /**
     * Title of this section.
     */
    stepTitle: string;
    /**
     * Subtitle of this section.
     */
    stepSubtitle: string;

    /**
     * Term groups when there is compensation for advances
     */
    termGroups: TermGroup[];

    /**
     * Whether form is meant for creation or edit
     */
    mode: "edit" | "new";
    /**
     * Advance Payment linked on term creation. Needed on edit.
     */
    linkedAdvancePayment: IAdvanceSlice | undefined;
    advancePayment: TAdvancePayment | null;
    setStateFunctions: {
        /**
         * Setting termHasAdvanceGroups.
         */
        setTermHasAdvanceGroups: Dispatch<SetStateAction<boolean>>;
        /**
         * Saves any toggle change of the "is term compensated with others" radio form.
         */
        setHasAdvanceGroupsOptionChanged: Dispatch<SetStateAction<boolean>>;
        /**
         * Sets createAdvanceInManagement.
         */
        setCreateAdvanceInManagement: Dispatch<SetStateAction<boolean | null>>;
        /**
         * Sets the advance payment data (slices) for the inline form.
         */
        setAdvancePayment: Dispatch<SetStateAction<TAdvancePayment | null>>;
    };
    booleanValues: Record<string, boolean | null | number>;
    currentPeriod: TPeriod | undefined;
    /**
     * EAN13 of the title — passed to InlineAdvanceForm for the "Go to Advances" navigation button.
     */
    titleEan13?: string;
}

/**
 * This component handles the inputs for the Settings section of the Term form.
 */
const SettingsStep = ({
    useFormData,
    indexStep,
    stepTitle,
    stepSubtitle,
    termGroups,
    mode,
    linkedAdvancePayment,
    advancePayment,
    booleanValues,
    setStateFunctions,
    formData,
    currentPeriod,
    titleEan13,
}: ISettingsStep) => {
    const [t] = useTranslation();
    const { usesCegidGect } = useCompanySettings();
    const { control, errors, setValue, clearErrors, dirtyFields, watch } = useFormData;
    const isTitlePublishedBeforePeriod = watch("isTitlePublishedBeforePeriod");
    const restoredAdvanceSlices = useMemo(() => {
        if (!advancePayment?.slices?.length) return undefined;
        return advancePayment.slices.map((slice) => ({
            ...slice,
            amount: slice.amount != null ? +slice.amount / 100 : "",
        }));
    }, [advancePayment]);

    const {
        usesAdvanceCompensation,
        usesTemplate,
        usesInitialAdvance,
        usesCumulativeRoyalties,
        termExistOnClosePeriod,
        termHasEnded,
        termHasAdvanceGroups,
        createAdvanceInManagement,
        advanceValidateSignal,
    } = booleanValues;

    const {
        setCreateAdvanceInManagement,
        setHasAdvanceGroupsOptionChanged,
        setTermHasAdvanceGroups,
        setAdvancePayment,
    } = setStateFunctions;
    const { chosenAdvance, chosenAdvanceGroup, chosenBeneficiary, chosenCumulativeRights, chosenInitialAdvance } =
        formData;

    const [showCompensationGroupModal, setShowCompensationGroupModal] = useState(false);
    const [newTermGroupId, setNewTermGroupId] = useState<number | null>(null);

    const compensationOptions = [
        { label: t("ConnectTitle.No") },
        { label: t("ConnectTitle.Yes"), defaultChecked: true },
    ];

    const isTitlePublishedBeforePeriodOptions = [{ label: t("ConnectTitle.No") }, { label: t("ConnectTitle.Yes") }];

    const minAdvancePaymentDate = useMemo(
        () => getTermFormMinAdvancePaymentDate(isTitlePublishedBeforePeriod, currentPeriod),
        [isTitlePublishedBeforePeriod, currentPeriod],
    );

    // Needed to handle default mode of the Radio Button on edit mode.
    const isChosenAdvanceGroupEmpty = chosenAdvanceGroup === undefined || chosenAdvanceGroup === null;

    // Creates string with names inside the advance groups titles[]
    const formatTermNames = (names: string[] | undefined) => {
        return names && names.join(", ");
    };

    // Determines whether collapse is open or not.
    const [openTitles, setOpenTitles] = useState(false);

    // Set which advance group to open, otherwise they all do.
    const [currentGroupForCollapse, setCurrentGroupForCollapse] = useState(null);

    useEffect(() => {
        if (termHasAdvanceGroups && mode == "new") {
            setValue && setValue("compensate", 1);
        }
    }, [termHasAdvanceGroups]);

    const handleTitlePublishedBeforePeriodChange = (value: string) => {
        if (!setValue || !shouldClearTitlePublishedBeforePeriodFields(mode, value)) {
            return;
        }

        clearTitlePublishedBeforePeriodFields(setValue, clearErrors);
    };

    const toggleTitlesList = (e: any, currentGroup: any) => {
        e.stopPropagation();
        setOpenTitles((prev) => !prev);
        currentGroupForCollapse === null ? setCurrentGroupForCollapse(currentGroup) : setCurrentGroupForCollapse(null);
    };

    // Handles toggling of whether or not term is compensated with another: both updating the value and triggering save btn
    const handlesAdvanceGroupsChange = () => {
        setTermHasAdvanceGroups((prev) => !prev);
        mode === "edit" && setHasAdvanceGroupsOptionChanged(true);
    };

    const renderTermGroupsOption = (props: any, option: any) => {
        // Sometimes the advance group titles[] has null inside. This filters it, otherwise it counts length as 1.
        const associatedTitles =
            option.titles !== undefined ? option.titles.filter((title) => title !== null) : option.titles;

        return !option.addElement ? (
            <li {...props} className="titles-groups-list">
                <div className="group-list--visible">
                    <div className="group-list--titles">
                        <span className="group-name">{option.name}</span>
                        {associatedTitles?.length > 0 && (
                            <span className="group-titles">
                                {t("CompensationGroups.AssociatedTitles")} {formatTermNames(associatedTitles)}
                            </span>
                        )}
                    </div>
                    {option.titles?.length > 2 && (
                        <div>
                            <TextButton
                                text={t("CompensationGroups.SeeTitles")}
                                handleClick={(e: any) => toggleTitlesList(e, option.id)}
                                style={{ fontSize: "12px" }}
                                underlineOnHover
                            ></TextButton>
                        </div>
                    )}
                </div>
                <Collapse in={openTitles && currentGroupForCollapse === option.id} timeout="auto" unmountOnExit>
                    <ScrollShadowWrapper className="group-list--collapse">
                        <div className="titles-list">
                            {option.titles?.map((title: string, i: number) => (
                                <p className="title-name" key={`${i}-title`}>
                                    {title ?? ""}
                                </p>
                            ))}
                        </div>
                    </ScrollShadowWrapper>
                </Collapse>
            </li>
        ) : (
            <li {...props} key={option.id} className="autocomplete-new-element">
                <div className="content">
                    <Plus />
                    {option.addElement}
                </div>
            </li>
        );
    };

    useEffect(() => {
        if (chosenAdvanceGroup?.id == -1) {
            setShowCompensationGroupModal(true);
            setValue && setValue("advanceCompensationGroup", null);
        }

        if (mode === "edit") {
            // Needed to update the value of the radio button on edit mode
            setTermHasAdvanceGroups(!isChosenAdvanceGroupEmpty);
        }
    }, [chosenAdvanceGroup]);

    useEffect(() => {
        if (newTermGroupId == null) return;
        setValue &&
            setValue("advanceCompensationGroup", termGroups?.find((group) => group.id === newTermGroupId) ?? null);
    }, [termGroups, newTermGroupId]);

    return (
        <>
            <NewGroupModal
                openModal={showCompensationGroupModal}
                onClose={() => setShowCompensationGroupModal(false)}
                type={"term"}
                elementId={chosenBeneficiary?.id}
                setNewGroupId={setNewTermGroupId}
            />
            <h3 className="form-section-title">
                {" "}
                {indexStep + 1}. {stepTitle}
            </h3>
            <span className="form-section-description">{stepSubtitle}</span>
            {termExistOnClosePeriod && (
                <p className="term-exist-on-close-period-warning">{t("Terms.TermExistOnClosedPeriod")}</p>
            )}

            <div className="form-fields settings">
                <div className="general-info">
                    <TitleSeparation
                        title={t("ConnectTitle.SettingsStep.Title1")}
                        titleStyle={{ fontSize: "0.9rem" }}
                        style={{ marginBottom: "1rem" }}
                    />
                    <div className="settings-balance">
                        <TextFormComponent
                            label={t("ConnectTitle.TermReference")}
                            control={control}
                            controlName="reference"
                            placeholder=""
                            error={errors.reference}
                        />
                        {usesCegidGect && (
                            <TextFormComponent
                                label={t("ConnectTitle.ContractTypology")}
                                control={control}
                                controlName="contractTypology"
                                placeholder={t("ConnectTitle.ContractTypologyPlaceholder")}
                                error={errors.contractTypology}
                                customValidate={(value) => !value || value.length <= 10}
                                customValidateMessage={t("ConnectTitle.ContractTypologyMaxLength")}
                            />
                        )}
                        <DateFormComponent
                            label={t("ConnectTitle.Date")}
                            control={control}
                            controlName="endDate"
                            error={errors.endDate}
                            disabled={!!termHasEnded}
                            disabledText={t("ServerError.CannotChangeEndedTermEndDate")}
                            maxYearDistance={30}
                        />
                    </div>
                    <div className="title-release-date">
                        <DateFormComponent
                            label={t("ConnectTitle.SettingsStep.TitleReleaseDate")}
                            control={control}
                            controlName="titleReleaseDate"
                            disabled
                            disabledText={t("ConnectTitle.SettingsStep.TitleReleaseDateDisabledTooltip")}
                            maxYearDistance={30}
                        />
                    </div>
                    {currentPeriod && (
                        <div className="general-info-period">
                            <span className="general-info-period__label">
                                {t("ConnectTitle.SettingsStep.PeriodLabel")}
                            </span>
                            <PeriodName
                                period={currentPeriod}
                                variant="termForm"
                                textStyle={{ fontWeight: "500", fontSize: "14px" }}
                            />
                        </div>
                    )}
                    <div className="title-published-on-period">
                        <RadioFormComponent
                            id="title-published-on-period"
                            label={t("ConnectTitle.SettingsStep.TitlePublishedBeforePeriod")}
                            control={control}
                            controlName="isTitlePublishedBeforePeriod"
                            error={errors.isTitlePublishedBeforePeriod}
                            options={isTitlePublishedBeforePeriodOptions}
                            required
                            labelAligned
                            onValueChange={handleTitlePublishedBeforePeriodChange}
                        />
                    </div>
                </div>

                <Collapse className="settings-sections-collapse" in={isTitlePublishedBeforePeriod == 1} unmountOnExit>
                    <div className="settings-sections-b">
                        <div className="period-data">
                            <TitleSeparation
                                title={t("ConnectTitle.SettingsStep.Title2")}
                                titleStyle={{ fontSize: "0.9rem" }}
                                style={{ marginBottom: "1rem" }}
                                description={t("ConnectTitle.SettingsStep.Title2Desc")}
                            />
                            <Fragment>
                                {usesInitialAdvance && (
                                    <NumberFormComponent
                                        label={`${t("ImportData.InitialAdvance")}`}
                                        control={control}
                                        controlName="initialAdvance"
                                        placeholder="-1000"
                                        error={+chosenInitialAdvance > 0 || errors.initialAdvance}
                                        float={true}
                                        icon={<CurrencyIcon sx={{ fontSize: 16 }} />}
                                        acceptsNegative
                                        description={t("Documentation.InitialAdvance")}
                                    />
                                )}
                                {usesCumulativeRoyalties && (
                                    <NumberFormComponent
                                        label={t("ConnectTitle.CumulativeRights")}
                                        control={control}
                                        controlName="cumulativeRights"
                                        placeholder="0"
                                        float
                                        error={errors.cumulativeRights}
                                        icon={<CurrencyIcon sx={{ fontSize: 16 }} />}
                                        description={t("Documentation.CumulativeRightsInfo")}
                                        acceptsNegative
                                    />
                                )}
                            </Fragment>
                            <NumberFormComponent
                                label={t("ConnectTitle.Provision")}
                                control={control}
                                controlName="initialProvision"
                                placeholder="0"
                                error={errors.initialProvision}
                                float={true}
                                disabled={!!termExistOnClosePeriod}
                                disabledText={t("ConnectTitle.ProvisionDisabled")}
                                icon={<CurrencyIcon sx={{ fontSize: 16 }} />}
                                description={t("ConnectTitle.ProvisionDescription")}
                            />
                            <OpeningBalanceField
                                booleanValues={{
                                    advanceAmountChanged: false,
                                    createAdvanceInManagement,
                                    usesTemplate,
                                    termExistOnClosePeriod,
                                    usesInitialAdvance,
                                }}
                                chosenAdvance={chosenAdvance}
                                useFormData={useFormData}
                            />
                        </div>
                    </div>
                </Collapse>

                <div className="settings-section-advance">
                    <TitleSeparation
                        title={t("ConnectTitle.SettingsStep.TitleAdvancePayment")}
                        titleStyle={{ fontSize: "0.9rem" }}
                        style={{ marginBottom: "1rem" }}
                    />
                    <div className="advance-section-container">
                        <InlineAdvanceForm
                            setAdvancePayment={setAdvancePayment}
                            setCreateAdvanceInManagement={setCreateAdvanceInManagement}
                            initialSlices={restoredAdvanceSlices}
                            mode={mode}
                            linkedAdvancePayment={linkedAdvancePayment}
                            validateSignal={advanceValidateSignal as number}
                            titleEan13={titleEan13}
                            minPaymentDate={minAdvancePaymentDate}
                        />
                    </div>
                </div>

                <div className="balance-settings">
                    <TitleSeparation
                        title={t("ConnectTitle.SettingsStep.Title3")}
                        titleStyle={{ fontSize: "0.9rem" }}
                        style={{ marginBottom: "1rem" }}
                    />
                    <div className="advance-compensation-radio">
                        <RadioFormComponent
                            id="term-compensate"
                            label={t("ConnectTitle.Compensate")}
                            control={control}
                            controlName="compensate"
                            error={errors.compensate}
                            options={compensationOptions}
                            disabled={!!(termHasAdvanceGroups || termExistOnClosePeriod)}
                        />
                    </div>
                    {usesAdvanceCompensation && (
                        <div className="advance-groups-option">
                            <div className="advance-label-group">
                                <FormLabel id="advance-group-radio" className="label">
                                    {t("CompensationGroups.AdvanceGroupsQuestion")}
                                </FormLabel>
                                <InfoTooltip
                                    iconType="question"
                                    text={t("ConnectTitle.SettingsStep.LinkCollateral")}
                                    link="https://helpdesk.crealo.app/fr/article/lier-les-soldes-de-plusieurs-titres-pour-un-meme-auteur-1nd8vym/"
                                />
                            </div>
                            <RadioGroup value={termHasAdvanceGroups} onChange={() => handlesAdvanceGroupsChange()} row>
                                <FormControlLabel
                                    value={false}
                                    control={<Radio checkedIcon={<CheckCircleIcon style={{ width: "15px" }} />} />}
                                    label={t("ConnectTitle.No")}
                                />
                                <FormControlLabel
                                    value={true}
                                    control={<Radio checkedIcon={<CheckCircleIcon style={{ width: "15px" }} />} />}
                                    label={t("ConnectTitle.Yes")}
                                />
                            </RadioGroup>
                        </div>
                    )}
                    {usesAdvanceCompensation && termHasAdvanceGroups && (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "5px",
                                marginTop: "1.5rem",
                            }}
                        >
                            <AutocompleteFormComponent
                                options={termGroups ?? []}
                                renderOptionText={(option) => option.name ?? ""}
                                renderOption={renderTermGroupsOption}
                                control={control}
                                error={errors.advanceCompensationGroup}
                                disabledText={t("NewTitle.GroupNotEditable")}
                                placeholder={t("NewTitle.NoCompensationGroup")}
                                controlName="advanceCompensationGroup"
                                label={
                                    mode === "new"
                                        ? t("ConnectTitle.TermGroupLabel")
                                        : t("CompensationGroups.AdvanceGroupName")
                                }
                                defaultFilterOptions
                                isOptionEqualToValue={(option, value) => option.name == value.name}
                                addElement={t("NewChannel.AddCompensationGroup")}
                                className="autocomplete-term-groups-compensation"
                                required
                            />
                            <span style={{ fontSize: "0.8rem", color: variables["foreground"] } as CSSProperties}>
                                {t("CompensationGroups.TermGroupInfo")}
                            </span>
                        </div>
                    )}
                </div>
            </div>
            <TermCustomFields section="settings" mode={mode} />
        </>
    );
};

export default SettingsStep;
